/**
 * KnowledgeAllSearchBar — vibe-designer 1.171.0 Phase 4.
 *
 * "Search all services" fan-out UI sibling to KnowledgeAskBox on the
 * Overview page. Backend fans out to all 6 services in parallel; results
 * grouped by service in collapsible sections (OQ-4.5 = B — interleaving
 * by score is impossible since CORTEX has no native similarity score).
 */
import { useState, type FormEvent } from 'react';
import { Loader2, ChevronDown, ChevronRight, Globe } from 'lucide-react';
import { useKnowledgeStore } from '@/stores/knowledgeStore';
import { useTenantStore } from '@/stores/tenantStore';
import { PillarChips } from './PillarChips';
import type {
  KnowledgeAllSearchResponse,
  KnowledgeSearchHit,
  KnowledgeServiceStats,
  WebSearchResult,
} from '@/services/knowledgeApi';

type ServiceName = KnowledgeServiceStats['service'];

const SERVICE_LABELS: Record<ServiceName, string> = {
  mind: 'THE MIND',
  cortex: 'THE CORTEX',
  brain: 'THE BRAIN',
  sight: 'THE SIGHT',
  cognition: 'THE COGNITION',
  obsidian: 'OBSIDIAN',
};

const SERVICE_ORDER: ServiceName[] = ['mind', 'cortex', 'brain', 'sight', 'cognition', 'obsidian'];

export function KnowledgeAllSearchBar() {
  const allSearchResponse = useKnowledgeStore((s) => s.allSearchResponse);
  const isSearchingAll = useKnowledgeStore((s) => s.isSearchingAll);
  const allSearchError = useKnowledgeStore((s) => s.allSearchError);
  const runAllSearch = useKnowledgeStore((s) => s.runAllSearch);
  // 1.188.0 Phase 7.4 — Web Search as optional 7th surface.
  const runWebSearch = useKnowledgeStore((s) => s.runWebSearch);
  const webSearchResults = useKnowledgeStore((s) => s.webSearchResults);
  const isWebSearching = useKnowledgeStore((s) => s.isWebSearching);
  const webSearchError = useKnowledgeStore((s) => s.webSearchError);
  const mcpCapabilities = useTenantStore((s) => s.mcpCapabilities);
  const webSearchAvailable = mcpCapabilities?.web_search ?? false;

  const [draft, setDraft] = useState('');
  // 1.184.0 Phase 7.1 — pillar applies server-side to MIND only; other
  // services get the kwarg as a Protocol no-op.
  const [pillar, setPillar] = useState<string | null>(null);
  // 1.188.0 Phase 7.4 — opt-in: keep 6-corpus surface as the default; user
  // checks the box when they actively want Web Search included. Disabled
  // when the capability is false (SearXNG down).
  const [includeWebSearch, setIncludeWebSearch] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<ServiceName | 'web_search', boolean>>({
    mind: false, cortex: false, brain: false, sight: false, cognition: false, obsidian: false,
    web_search: false,
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = draft.trim();
    if (!q) return;
    void runAllSearch(q, undefined, pillar ? { pillar_filter: pillar } : undefined);
    // Phase 7.4 — fire Web Search in parallel when opted in AND capable.
    // Two separate store actions (independent loading/error states) — the
    // 6-corpus fan-out cannot block on a slow SearXNG aggregator, and
    // SearXNG failure must not poison the 6-corpus results.
    if (includeWebSearch && webSearchAvailable) {
      void runWebSearch(q);
    }
  };

  const toggle = (svc: ServiceName | 'web_search') =>
    setCollapsed((c) => ({ ...c, [svc]: !c[svc] }));

  return (
    <section className="border-t border-vibe-border pt-3 mt-3">
      <header className="text-xs font-semibold uppercase tracking-wide text-vibe-text-secondary mb-2">
        Search all services
      </header>
      <PillarChips value={pillar} onChange={setPillar} className="mb-3" />
      <label
        data-testid="knowledge-all-search-include-web"
        className={`flex items-center gap-2 mb-3 text-xs ${
          webSearchAvailable
            ? 'text-vibe-text-secondary cursor-pointer'
            : 'text-vibe-text-secondary/50 cursor-not-allowed'
        }`}
        title={
          webSearchAvailable
            ? 'Include the SearXNG web aggregator alongside the 6 corpora'
            : 'Web Search unavailable for this tenant (SearXNG probe failed)'
        }
      >
        <input
          type="checkbox"
          checked={includeWebSearch && webSearchAvailable}
          onChange={(e) => setIncludeWebSearch(e.target.checked)}
          disabled={!webSearchAvailable}
          data-testid="knowledge-all-search-include-web-checkbox"
          className="accent-blue-500"
        />
        <Globe className="w-3 h-3" />
        <span>Include Web Search</span>
      </label>
      <form onSubmit={onSubmit} className="flex gap-2 mb-3">
        <input
          data-testid="knowledge-all-search-input"
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Search across all 6 services…"
          className="flex-1 px-3 py-1.5 text-sm rounded bg-vibe-bg border border-vibe-border text-vibe-text placeholder:text-vibe-text-secondary"
          disabled={isSearchingAll}
        />
        <button
          data-testid="knowledge-all-search-submit"
          type="submit"
          disabled={isSearchingAll || draft.trim().length === 0}
          className="px-3 py-1.5 text-sm rounded bg-vibe-accent text-vibe-bg disabled:opacity-50"
        >
          {isSearchingAll ? 'Searching…' : 'Search'}
        </button>
      </form>

      {isSearchingAll && (
        <div
          data-testid="knowledge-all-search-loading"
          className="flex items-center gap-2 text-sm text-vibe-text-secondary py-2"
        >
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Searching all 6 services in parallel…</span>
        </div>
      )}

      {!isSearchingAll && allSearchError && (
        <div
          data-testid="knowledge-all-search-network-error"
          className="rounded border border-vibe-error/30 bg-vibe-error/10 p-3 text-sm text-vibe-error"
        >
          Network error: {allSearchError.message}
        </div>
      )}

      {!isSearchingAll && allSearchResponse && allSearchResponse.total_hits === 0 &&
        Object.values(allSearchResponse.results_by_service).every((r) => !r.error) && (
        <div
          data-testid="knowledge-all-search-empty"
          className="text-sm text-vibe-text-secondary py-2"
        >
          No results across any service for «{allSearchResponse.q}».
        </div>
      )}

      {/* 1.192.0 Phase 7.7 (§5.4 issue 2): summary error banner. When any
          per-service result carries an error AND the user has collapsed groups,
          the error message stays hidden inside the body. This banner surfaces
          the count + names ABOVE the groups so the user knows something failed
          before they decide which group to expand. Visible even when all groups
          remain collapsed. */}
      {!isSearchingAll && allSearchResponse && (() => {
        const erroredServices = SERVICE_ORDER.filter(
          (svc) => allSearchResponse.results_by_service[svc]?.error,
        );
        if (erroredServices.length === 0) return null;
        return (
          <div
            data-testid="knowledge-all-search-has-errors"
            className="rounded border border-vibe-warning/30 bg-vibe-warning/10 p-2 text-xs text-vibe-warning"
          >
            {erroredServices.length} service{erroredServices.length === 1 ? '' : 's'} returned errors:{' '}
            {erroredServices.map((s) => SERVICE_LABELS[s]).join(', ')}
          </div>
        );
      })()}

      {!isSearchingAll && allSearchResponse && (
        <div className="space-y-2">
          {SERVICE_ORDER.map((svc) => (
            <ServiceGroup
              key={svc}
              service={svc}
              data={allSearchResponse.results_by_service[svc]}
              collapsed={collapsed[svc]}
              onToggle={() => toggle(svc)}
            />
          ))}
          {/* 1.188.0 Phase 7.4 — 7th surface: render Web Search results when
              opted-in and loaded. Independent of allSearchResponse (different
              backend route, different shape). Rendered alongside the 6 corpora
              for unified UX even though the API call paths are separate. */}
          {includeWebSearch && webSearchAvailable && (
            // q fallback to allSearchResponse.q is safe: this block is
            // already gated by `!isSearchingAll && allSearchResponse` above,
            // so allSearchResponse cannot be null here.
            <WebSearchGroup
              loading={isWebSearching}
              error={webSearchError}
              results={webSearchResults?.results ?? null}
              q={webSearchResults?.query ?? allSearchResponse.q}
              collapsed={collapsed.web_search}
              onToggle={() => toggle('web_search')}
            />
          )}
        </div>
      )}
    </section>
  );
}

interface WebSearchGroupProps {
  loading: boolean;
  error: Error | null;
  results: WebSearchResult[] | null;
  q: string;
  collapsed: boolean;
  onToggle: () => void;
}

function WebSearchGroup({ loading, error, results, q, collapsed, onToggle }: WebSearchGroupProps) {
  const hits = results ?? [];
  return (
    <div
      data-testid="knowledge-all-search-group-web-search"
      className="rounded border border-vibe-border bg-vibe-bg"
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-3 py-2 flex items-center justify-between text-sm text-vibe-text hover:bg-vibe-bg-tertiary"
      >
        <span className="flex items-center gap-2">
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          <Globe className="w-3 h-3 text-blue-400" />
          <span className="font-medium">Web Search</span>
          <span className="text-vibe-text-secondary text-xs">
            ({loading ? '…' : hits.length} hit{hits.length === 1 ? '' : 's'})
          </span>
        </span>
        {error && !loading && (
          <span className="text-vibe-error text-xs">error</span>
        )}
      </button>
      {!collapsed && (
        <div className="px-3 pb-3 space-y-2">
          {loading && (
            <div className="flex items-center gap-2 text-xs text-vibe-text-secondary">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>Searching {q}…</span>
            </div>
          )}
          {error && !loading && (
            <div className="rounded border border-vibe-error/30 bg-vibe-error/10 p-2 text-xs text-vibe-error">
              {error.message}
            </div>
          )}
          {!loading && !error && hits.length === 0 && (
            <div className="text-xs text-vibe-text-secondary">No web results.</div>
          )}
          {hits.map((hit, i) => (
            <div
              key={`web-${i}`}
              data-testid={`knowledge-all-search-result-web-search-${i}`}
              className="rounded border border-vibe-border p-2"
            >
              <a
                href={hit.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-vibe-accent hover:underline truncate block mb-1"
              >
                {hit.title}
              </a>
              <div className="text-xs text-vibe-text-secondary whitespace-pre-wrap max-h-20 overflow-hidden">
                {hit.snippet}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface ServiceGroupProps {
  service: ServiceName;
  data: KnowledgeAllSearchResponse['results_by_service'][ServiceName] | undefined;
  collapsed: boolean;
  onToggle: () => void;
}

function ServiceGroup({ service, data, collapsed, onToggle }: ServiceGroupProps) {
  if (!data) {
    return (
      <div data-testid={`knowledge-all-search-group-${service}`} className="rounded border border-vibe-border p-2 text-xs text-vibe-text-secondary">
        {SERVICE_LABELS[service]}: no response
      </div>
    );
  }
  const hits = data.hits || [];
  return (
    <div
      data-testid={`knowledge-all-search-group-${service}`}
      className="rounded border border-vibe-border bg-vibe-bg"
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-3 py-2 flex items-center justify-between text-sm text-vibe-text hover:bg-vibe-bg-tertiary"
      >
        <span className="flex items-center gap-2">
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          <span className="font-medium">{SERVICE_LABELS[service]}</span>
          <span className="text-vibe-text-secondary text-xs">
            ({hits.length} hit{hits.length === 1 ? '' : 's'})
          </span>
        </span>
        {!data.available && (
          <span className="text-vibe-warning text-xs">unavailable</span>
        )}
      </button>
      {!collapsed && (
        <div className="px-3 pb-3 space-y-2">
          {data.error && (
            <div
              data-testid={`knowledge-all-search-service-error-${service}`}
              className="rounded border border-vibe-warning/30 bg-vibe-warning/10 p-2 text-xs text-vibe-warning"
            >
              {data.error}
            </div>
          )}
          {hits.length === 0 && !data.error && (
            <div className="text-xs text-vibe-text-secondary">No results.</div>
          )}
          {hits.map((hit: KnowledgeSearchHit, i: number) => (
            <div
              key={`${service}-${i}`}
              data-testid={`knowledge-all-search-result-${service}-${i}`}
              className="rounded border border-vibe-border p-2"
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-sm font-medium text-vibe-text truncate">{hit.title}</span>
                {hit.score !== null && (
                  <span className="text-xs tabular-nums text-vibe-text-secondary">{hit.score.toFixed(3)}</span>
                )}
              </div>
              <div className="text-xs text-vibe-text-secondary whitespace-pre-wrap max-h-20 overflow-hidden">
                {hit.snippet}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
